import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '../api';
import type { OpeningBalance, Party } from '../api';
import { SectionCard } from '../ui/components';
import { ActionQueueCard } from '../ui/mobileOps';
import {
  Button,
  ErrorBanner,
  Select,
  Spinner,
  TextArea,
  TextInput,
} from '../ui/primitives';
import { inrToKinrPreview, inrToKinrString } from '../money/inr';

export function StepOpeningBalances() {
  const qc = useQueryClient();
  const parties = useQuery<Party[]>({
    queryKey: ['parties'],
    queryFn: () => api.get<Party[]>('/api/parties'),
  });
  const balances = useQuery<OpeningBalance[]>({
    queryKey: ['opening-balances'],
    queryFn: () => api.get<OpeningBalance[]>('/api/opening-balances'),
  });

  const [form, setForm] = useState({
    partyId: '',
    asOfDate: '',
    amount: '',
    note: '',
    source: 'TRADE_TIME_CARRYOVER',
  });
  const [error, setError] = useState<string | null>(null);

  const upsertMut = useMutation({
    mutationFn: async () => {
      if (!form.partyId) throw new Error('Pick a party.');
      if (!form.asOfDate) throw new Error('As-of date is required.');
      if (!form.amount.trim()) throw new Error('Amount is required.');
      if (!form.note.trim()) throw new Error('Note is required.');
      const kinrAmount = inrToKinrString(form.amount);
      if (kinrAmount === null) throw new Error('Amount is not a valid number.');
      return api.put<OpeningBalance>('/api/opening-balances', {
        partyId: Number(form.partyId),
        asOfDate: form.asOfDate,
        amount: kinrAmount,
        note: form.note.trim(),
        source: form.source,
      });
    },
    onSuccess: () => {
      setForm((prev) => ({ ...prev, amount: '', note: '' }));
      setError(null);
      void qc.invalidateQueries();
    },
    onError: (err) => setError(err instanceof ApiError ? err.message : String(err)),
  });

  const deleteMut = useMutation({
    mutationFn: (id: number) => api.delete<void>(`/api/opening-balances/${id}`),
    onSuccess: () => void qc.invalidateQueries(),
  });

  const nonSelfParties = (parties.data ?? []).filter((party) => party.role !== 'self');
  const partyById = new Map(nonSelfParties.map((party) => [party.id, party.displayName]));
  const preview = form.amount.trim().length > 0 ? inrToKinrPreview(form.amount) : null;

  return (
    <div className="space-y-4">
      <ActionQueueCard
        section="accounts"
        eyebrow="Step 5"
        title="Carry forward the balances you already trust"
        description="Enter the starting balance for each non-self party once. Positive means they owe you; negative means you owe them."
        meta={
          <div className="space-y-2">
            <div className="flex items-center justify-between gap-3">
              <span>Parties available</span>
              <span className="font-medium text-ink">{nonSelfParties.length}</span>
            </div>
            <div className="flex items-center justify-between gap-3">
              <span>Saved balances</span>
              <span className="font-medium text-ink">{balances.data?.length ?? 0}</span>
            </div>
            <div className="text-[12px] leading-5 text-ink-muted">
              Enter rupees here. The stored value is converted into K-INR automatically.
            </div>
          </div>
        }
      />

      <SectionCard section="accounts" title="Opening balances">
        <form
          className="grid gap-3 sm:grid-cols-2"
          onSubmit={(e) => {
            e.preventDefault();
            upsertMut.mutate();
          }}
        >
          <Select
            label="Party"
            value={form.partyId}
            onChange={(value) => setForm({ ...form, partyId: value })}
            options={nonSelfParties.map((party) => ({ value: String(party.id), label: `${party.displayName} (${party.role})` }))}
            placeholder="Choose..."
          />
          <TextInput
            label="As-of date"
            type="date"
            value={form.asOfDate}
            onChange={(e) => setForm({ ...form, asOfDate: e.target.value })}
          />
          <div className="flex flex-col">
            <TextInput
              label="Amount (₹)"
              value={form.amount}
              onChange={(e) => setForm({ ...form, amount: e.target.value })}
              placeholder="e.g. -192024"
              inputMode="decimal"
              hint="Positive = party owes you. Negative = you owe them."
            />
            {form.amount.trim().length > 0 && (
              <p className={`mt-1 text-caption ${preview ? 'text-pos' : 'text-neg'}`}>
                {preview ?? 'Not a valid amount'}
                {preview && <span className="ml-1 text-ink-muted">(stored value)</span>}
              </p>
            )}
          </div>
          <TextInput
            label="Source"
            value={form.source}
            onChange={(e) => setForm({ ...form, source: e.target.value })}
            hint="Where this number came from."
          />
          <div className="col-span-full">
            <TextArea
              label="Note"
              rows={2}
              value={form.note}
              onChange={(e) => setForm({ ...form, note: e.target.value })}
              placeholder="e.g. carried from TRADE-TIME Gori ledger closing balance"
            />
          </div>
          {error && (
            <div className="col-span-full">
              <ErrorBanner>{error}</ErrorBanner>
            </div>
          )}
          <div className="col-span-full">
            <Button type="submit" disabled={upsertMut.isPending}>
              {upsertMut.isPending ? <Spinner /> : 'Save opening balance'}
            </Button>
          </div>
        </form>
      </SectionCard>

      <SectionCard section="accounts" title="Recorded opening balances">
        {balances.isLoading ? (
          <div className="flex items-center gap-2 text-small text-ink-muted">
            <Spinner /> loading...
          </div>
        ) : balances.data && balances.data.length > 0 ? (
          <div className="space-y-3">
            {balances.data.map((balance) => (
              <div
                key={balance.id}
                className="flex flex-wrap items-start justify-between gap-3 rounded-[22px] border border-border/70 bg-surface px-4 py-3 shadow-sm backdrop-blur"
              >
                <div className="min-w-0 flex-1">
                  <div className="text-small font-semibold tracking-[-0.01em] text-ink">
                    {partyById.get(balance.partyId) ?? `#${balance.partyId}`}
                  </div>
                  <div className="mt-1 text-[12px] leading-5 text-ink-muted">
                    {balance.asOfDate} · {balance.amount} K · {balance.source}
                  </div>
                  <div className="mt-1 text-[12px] leading-5 text-ink-muted">{balance.note}</div>
                </div>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => {
                    if (confirm('Delete this opening balance?')) deleteMut.mutate(balance.id);
                  }}
                >
                  Delete
                </Button>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-caption text-ink-muted">No opening balances yet.</p>
        )}
      </SectionCard>
    </div>
  );
}
